import Echart from "echarts-for-react"
import { useState } from "react"
import CustomLegend from "./weekday-detail-chart-custom-legend"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { detailChartColors, getLegends, weekdayBundle, weekdayKeys } from "./weekday-const"

const WeekdayDetailChart = ({ series, maxWeeks = 5, isMobile = false }) => {
  const { lang } = langStore()
  const weekLabels = weekdayKeys.map((key) => weekdayBundle.days[key][lang])
  const legends = getLegends(lang)

  const [filterIds, setFilterIds] = useState(["two", "one", "curr"])

  const seriesFilter = (id) => {
    setFilterIds((prev) => {
      const isActive = prev.includes(id)

      if (isActive && prev.length === 1) {
        return prev
      }

      if (isActive) {
        return prev.filter((v) => v !== id)
      }

      return [...prev, id]
    })
  }

  const filteredSeries = series.filter((s) => filterIds.includes(s.stack))

  const getWeekLabel = (name) => {
    const match = String(name).match(/(\d+)/)
    const week = match?.[1] ?? name
    return formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week })
  }

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const map = {}
        let currSum = 0,
          oneSum = 0,
          twoSum = 0

        params.forEach((p) => {
          if (p.value === 0) return

          const key = p.seriesName

          if (!map[key]) {
            map[key] = { name: key, curr: "", one: "", two: "" }
          }

          const isCurr = p.seriesId.includes("curr")
          const isOne = p.seriesId.includes("one")

          if (isCurr) {
            map[key].curr = p.value.toFixed(2)
            currSum += p.value
          } else if (isOne) {
            map[key].one = p.value.toFixed(2)
            oneSum += p.value
          } else {
            map[key].two = p.value.toFixed(2)
            twoSum += p.value
          }
        })

        const rows = Object.values(map)

        const activeLegends = legends.filter((l) => filterIds.includes(l.id))
        const buildTh = () => {
          return activeLegends
            .map(
              ({ id, label }) =>
                `<th style="padding:2px 6px; color:${detailChartColors[id]};">${label}</th>`
            )
            .join("")
        }

        const buildRow = (r) => {
          return `
            <tr>
              <td style="padding:2px 6px;">${getWeekLabel(r.name)}</td>
              ${activeLegends
                .map(({ id }) => {
                  const color = detailChartColors[id]
                  return `<td style="padding:2px 6px; text-align:right; color:${color};">${r[id] || "-"}</td>`
                })
                .join("")}
            </tr>
          `
        }

        const buildSum = () => {
          const sums = { two: twoSum, one: oneSum, curr: currSum }

          return activeLegends
            .map(({ id }) => {
              const color = detailChartColors[id]
              return `<td style="padding:4px 6px; text-align:right; color:${color};">${sums[id].toFixed(2)}</td>`
            })
            .join("")
        }

        return `
          <div style="padding:6px;">
            <div style="font-weight:bold; margin-bottom:6px;">
              ${params[0].name}${weekdayBundle.detailModal.daySuffix[lang]}
            </div>

            <table style="border-collapse:collapse; font-size:11px;">
              <thead>
                <tr style="border-bottom:1px solid #eee;">
                  <th style="text-align:left; padding:2px 6px;">${weekdayBundle.detailModal.weekHeader[lang]}</th>
                  ${buildTh()}
                </tr>
              </thead>
              <tbody>
                ${rows.map(buildRow).join("")}
              </tbody>
              <tfoot>
                <tr style="border-top:1px solid #eee; font-weight:bold;">
                  <td style="padding:4px 6px;">${weekdayBundle.detailModal.total[lang]}</td>
                  ${buildSum()}
                </tr>
              </tfoot>
            </table>
          </div>
        `
      }
    },
    grid: {
      top: 30,
      left: "3%",
      right: "3%",
      bottom: "12%",
      containLabel: true
    },
    legend: {
      show: true,
      bottom: "3%",
      inactiveColor: "#E2E2E2",
      data: [
        {
          name: formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week: 1 }),
          itemStyle: { color: "#1E293B" }
        },
        {
          name: formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week: 2 }),
          itemStyle: { color: "#334155" }
        },
        {
          name: formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week: 3 }),
          itemStyle: { color: "#64748B" }
        },
        {
          name: formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week: 4 }),
          itemStyle: { color: "#94A3B8" }
        },
        {
          name: formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week: 5 }),
          itemStyle: { color: "#CBD5E1" }
        },
        {
          name: formatMessage(weekdayBundle.detailModal.weekLegend.format[lang], { week: 6 }),
          itemStyle: { color: "#F1F5F9" }
        }
      ].slice(0, maxWeeks)
    },
    xAxis: isMobile
      ? {
          type: "value"
        }
      : {
          type: "category",
          data: weekLabels
        },

    yAxis: isMobile
      ? {
          type: "category",
          data: weekLabels
        }
      : {
          type: "value"
        },
    series: filteredSeries.map((s) => ({
      ...s,
      name: getWeekLabel(s.name),
      barWidth: isMobile ? 10 : 20,
      barGap: isMobile ? "25%" : "40%"
    }))
  }

  if (series.length === 0) return <div className="w-full" />

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <CustomLegend legends={legends} filterIds={filterIds} onClick={seriesFilter} />
      <Echart notMerge style={{ height: "100%" }} option={option} />
    </div>
  )
}

export default WeekdayDetailChart
